/**
 * Debug node-ID overlay (`/debug` command).
 *
 * When ON, superimposes a small pill over each outline bullet showing that node's
 * compact **type code** (RFI, MS, PH, CONTRACTS, THREADS…) derived from the stable
 * node key (the `data-key` the engine + React both put on every `.wf-node`). Repeated
 * types get a 1-based index (RFI·1, RFI·2) so they disambiguate at a glance.
 *
 * **Click a badge to copy its full node key to the clipboard** — `ms:recXXX`,
 * `contract:<uuid>`, `sec:phase:recXXX:threads`, etc., the exact identifier every
 * zoom/collapse/breadcrumb path uses. So you paste Claude the precise key instead of
 * reading a long UUID off screen. (The full key is also in the badge `title` on hover.)
 *
 * Fully component-agnostic: it reads the DOM, never the React tree, so it works for
 * engine-rendered HTML and React sections alike, at every zoom level. State persists
 * across reloads in localStorage (`aj-debug`), mirroring theme.js.
 *
 * Robustness: badges are re-painted on every `outline-render` AND on any DOM mutation
 * under the outline containers (a MutationObserver), so a React re-render that wipes a
 * row can't leave the overlay stale. The observer is disconnected while we paint so our
 * own badge insertions never re-trigger it.
 *
 * Gated to "manager and up" seats. `isEligible()` checks tenancy.js's cached seat LIVE on every
 * call rather than a one-time snapshot — a snapshot taken right after `loadCurrentUserSeat()`
 * resolves would have a real race window (anyone running /debug before that async Supabase call
 * finishes gets blocked regardless of their actual seat). Checking live means eligibility is
 * always correct by the time a human actually types /debug, seconds after page load.
 * `initDebugGate()`, called once from OutlineView after the seat loads, only handles restoring
 * (or clearing) whatever was persisted in localStorage from a previous session.
 */

import { isManagerOrAbove } from '../services/tenancy.js'

const STORE = 'aj-debug'

let enabled = false
let wired = false
let raf = 0
let observer = null
let indicatorEl = null

function clearBadges() {
  document.querySelectorAll('.aj-debug-badge, .aj-debug-modal-badge').forEach(b => b.remove())
}

/** Copy text to the clipboard (Clipboard API with an execCommand fallback). */
function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      ta.remove()
      resolve()
    } catch (e) { reject(e) }
  })
}

/**
 * Compact, readable code for a node key. The TYPE is what's useful at a glance
 * (it maps 1:1 to a component); the full key (incl. record id / UUID) stays on hover.
 *   ms:recXXX                       -> MS
 *   ph:recXXX                       -> PH
 *   rfi:<uuid>                      -> RFI
 *   contract:<uuid>                 -> CONTRACT
 *   root:schedule                   -> SCHEDULE
 *   sec:construction:contracts      -> CONTRACTS
 *   sec:contract:<uuid>:rfis        -> RFIS
 *   sec:phase:recXXX:threads        -> THREADS
 */
function shortType(key) {
  const segs = key.split(':')
  const t = (segs[0] === 'sec' || segs[0] === 'root') ? segs[segs.length - 1] : segs[0]
  return (t || key).toUpperCase()
}

/**
 * The node key, resolved robustly. Many React section/root nodes put `data-key` on the bullet or the
 * menu button (for the engine's click handlers) rather than on the `.wf-node` wrapper — and a few omit
 * it entirely. So: read the wrapper first, then any `[data-key]` inside the node's OWN row (not a
 * child node's row). Returns null when nothing in the DOM carries a key.
 */
function keyForNode(node, row) {
  const own = node.getAttribute('data-key')
  if (own) return own
  if (row) {
    const keyed = row.querySelector('[data-key]')
    if (keyed) { const k = keyed.getAttribute('data-key'); if (k) return k }
  }
  return null
}

/** Fallback type label from the most specific `wf-node--<modifier>` class (for keyless nodes). */
function typeFromClass(node) {
  const mods = Array.from(node.classList)
    .filter(c => c.startsWith('wf-node--') && c !== 'wf-node--collapsed')
    .map(c => c.slice('wf-node--'.length))
  // Prefer a semantic modifier (item/section/root/phase/concept/cat) over a layout one.
  const best = mods.find(m => /^(item|section|root|phase|concept|cat|ms|project)$/.test(m)) || mods[0]
  return (best || 'node').toUpperCase()
}

/** Paint a fixed badge over each open modal's card (identified by [data-modal-id]). */
function paintModals() {
  if (!enabled) return
  document.querySelectorAll('[data-modal-id]').forEach(card => {
    const id = card.getAttribute('data-modal-id')
    if (!id) return
    const rect = card.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const badge = document.createElement('button')
    badge.type = 'button'
    badge.className = 'aj-debug-modal-badge'
    badge.textContent = id
    badge.title = 'Click to copy: ' + id
    badge.style.top = (rect.top + 8) + 'px'
    badge.style.left = (rect.left + 8) + 'px'
    badge.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
      copyText(id).then(() => {
        badge.classList.add('aj-debug-modal-badge--copied')
        setTimeout(() => badge.classList.remove('aj-debug-modal-badge--copied'), 900)
      }).catch(() => {})
    })
    document.body.appendChild(badge)
  })
}

/** Append one short type-code pill over each node's own bullet. */
function paint() {
  clearBadges()
  if (!enabled) return
  // Every node, not just those with a wrapper data-key — keyless/structurally-different nodes still
  // get a (non-copyable) type badge so /debug is visible on the WHOLE tree.
  const nodes = document.querySelectorAll('#aj-sc-root .wf-node, #aj-sc-sections .wf-node')

  // Pass 1: collect visible nodes + count each type (so we only number when it disambiguates).
  const entries = []
  const counts = {}
  nodes.forEach(node => {
    const row = node.querySelector(':scope > .wf-row')
    if (!row) return
    const bullet = row.querySelector('.wf-bullet, .wf-item-circle')
    if (!bullet) return
    const key = keyForNode(node, row)
    const type = key ? shortType(key) : typeFromClass(node)
    counts[type] = (counts[type] || 0) + 1
    entries.push({ bullet, key, type })
  })

  // Pass 2: render — append a per-type index (RFI·1, RFI·2) only when the type repeats.
  // Each badge copies its full node key on click (class-only feedback, so the
  // MutationObserver — which watches childList, not attributes — never repaints over it).
  // Keyless nodes still show a type badge but are visibly muted and copy nothing.
  const seen = {}
  entries.forEach(({ bullet, key, type }) => {
    seen[type] = (seen[type] || 0) + 1
    const badge = document.createElement('span')
    badge.className = 'aj-debug-badge' + (key ? '' : ' aj-debug-badge--nokey')
    badge.textContent = counts[type] > 1 ? type + '·' + seen[type] : type
    badge.title = key ? ('Click to copy: ' + key) : (type + ' — no node key (not separately addressable)')
    if (key) {
      badge.addEventListener('click', e => {
        e.preventDefault()
        e.stopPropagation()   // don't let the click zoom the bullet underneath
        copyText(key).then(() => {
          badge.classList.add('aj-debug-badge--copied')
          setTimeout(() => badge.classList.remove('aj-debug-badge--copied'), 900)
        }).catch(() => {})
      })
    }
    bullet.appendChild(badge)
  })

  paintModals()
}

function observeTargets() {
  if (!observer) return
  const root = document.getElementById('aj-sc-root')
  const sections = document.getElementById('aj-sc-sections')
  if (root) observer.observe(root, { childList: true, subtree: true })
  if (sections) observer.observe(sections, { childList: true, subtree: true })
  // Watch body's direct children for modal portals opening/closing.
  observer.observe(document.body, { childList: true })
}

function scheduleRefresh() {
  if (raf) cancelAnimationFrame(raf)
  raf = requestAnimationFrame(() => {
    raf = 0
    if (observer) observer.disconnect()   // don't let our own writes re-trigger us
    paint()
    if (enabled) observeTargets()
  })
}

function wire() {
  if (wired) return
  wired = true
  window.addEventListener('outline-render', scheduleRefresh)
  observer = new MutationObserver(scheduleRefresh)
}

/** Always-in-front "debug mode is on" pill, bottom-right. Click it to turn debug mode off —
 *  same effect as running /debug again. */
function showIndicator() {
  if (indicatorEl || typeof document === 'undefined') return
  indicatorEl = document.createElement('button')
  indicatorEl.type = 'button'
  indicatorEl.id = 'aj-debug-indicator'
  indicatorEl.textContent = 'Debug mode'
  indicatorEl.title = 'Debug mode is on — click to turn off'
  indicatorEl.addEventListener('click', () => setDebug(false))
  document.body.appendChild(indicatorEl)
}

function hideIndicator() {
  if (indicatorEl) { indicatorEl.remove(); indicatorEl = null }
}

export function isDebug() {
  return enabled
}

/** True if the current user's seat is manager-and-up — checked live, not cached. */
export function isEligible() {
  return isManagerOrAbove()
}

export function setDebug(on) {
  if (on && !isEligible()) return false
  enabled = !!on
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.toggle('aj-debug', enabled)
  }
  try { localStorage.setItem(STORE, enabled ? '1' : '0') } catch (e) {}
  wire()
  if (enabled) {
    observeTargets()
    scheduleRefresh()
    showIndicator()
  } else {
    if (observer) observer.disconnect()
    clearBadges()
    hideIndicator()
  }
  return true
}

/**
 * Called once at startup (OutlineView, right after the Supabase seat resolves) to restore
 * whatever was persisted in localStorage from a previous session — ON only if the seat that just
 * loaded is manager-and-up, otherwise the stale flag is cleared so it can't linger for someone
 * who isn't (or is no longer) eligible. The live isEligible() check below is what actually gates
 * every /debug invocation — this function only handles the one-time page-load restore.
 */
export function initDebugGate() {
  let wantOn = false
  try { wantOn = localStorage.getItem(STORE) === '1' } catch (e) {}
  if (!isEligible()) {
    if (wantOn) { try { localStorage.removeItem(STORE) } catch (e) {} }
    if (enabled) setDebug(false)
    return
  }
  if (wantOn) setDebug(true)
}
